import React, { useState, useEffect } from "react";
import { Table } from "antd";
import { CustomDropdown } from "../../components/visuals/PageFilter/PageFilter";
import {
  StyledTable,
  TableContainer,
} from "../../components/tables/styledComponents";

import {
  CustomTableCell,
  CustomTableCellProps,
} from "../../components/tables/CustomTableCell";
import {
  buildMyFundsDataSource,
  getAllMyFundsColumns,
  getValueByPortfolioIndexIdAndKey,
  isRecordIndex,
  addIndexSelectorName,
  getColumnTypeFromKeyOrValue,
  getComparatorIndexFundKey,
  getIndexBySelectorName,
  getFontColorFromValuesAndColumnType,
  buildIndexOptions,
} from "./utils";
import { placeholderData } from "./mockData";
import {
  CellType,
  ColumnType,
  Metric,
  MyFundsDataSourceType,
} from "../../models/tableModels";
import { Filter as Option } from "../../types/PageFilterTypes";
import { MY_FUNDS_BASE_COLUMNS } from "./constants";
import { MESSAGES } from "../../messages";
import { DeletePortfolioProps } from "./DeletePortfolio";

export const MyFundsTable: React.FC<MyFundsTableProps> = ({
  selectedColumns,
  fundsData,
  selectedDefaultBenchmark,
}) => {
  const { Column } = Table;
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedIndexes, setSelectedIndexes] = useState<SelectedIndexes>({});
  const [dataSources, setDataSources] = useState<MyFundsDataSourceType[][]>(
    buildMyFundsDataSource(placeholderData)
  );
  const columns = getAllMyFundsColumns(MY_FUNDS_BASE_COLUMNS, selectedColumns);
  const dataSource: MyFundsDataSourceType[] = dataSources[0];
  const indexes: MyFundsDataSourceType[] = dataSources[1];

  useEffect(() => {
    handleLoadedFundsData();
  }, [fundsData]);

  const handleLoadedFundsData = (): void => {
    if (fundsData.length > 0) {
      setDataSources(buildMyFundsDataSource(fundsData));
      setIsLoading(false);
    }
  };

  /**
   * Updates the selected indexes based on the dropdown selection.
   * @param option - The selected dropdown option.
   */
  const OnSelectedIndex = (option: Option): void => {
    setSelectedIndexes((prevSelectedIndexes) => ({
      ...prevSelectedIndexes,
      [option.selectorName as string]: option,
    }));
  };

  /**
   * Dynamically returns the value of a specific column for a given record key.
   *
   * @param recordKey - The key of the record.
   * @param column - The column object.
   * @returns The value of the column for the given record key.
   */
  const displayIndexValue = (recordKey: string, column: Option): CellType => {
    const indexId =
      getIndexBySelectorName(recordKey, selectedIndexes) ||
      (indexes[0]?.portfolio_index_id as string);

    return getValueByPortfolioIndexIdAndKey(
      indexes,
      indexId,
      column.key as string
    );
  };

  /**
   * Retrieves custom cell data based on comparisons of column and value.
   * @param record - Data source containing fund information.
   * @param column - Column option indicating the data being compared.
   * @param value - The value to be compared.
   * @returns Custom table cell properties including type, value, and font color.
   */
  const getCustomCellData = (
    record: MyFundsDataSourceType,
    column: Option,
    value: string | number | boolean | DeletePortfolioProps
  ): CustomTableCellProps => {
    const columnType: ColumnType = getColumnTypeFromKeyOrValue(
      column.key as string,
      value,
      isLoading
    );
    const cellType = columnType.type;

    if (!record.is_index) {
      const comparedIndexKey = getComparatorIndexFundKey(record.key as string);
      const comparedIndexValue = displayIndexValue(comparedIndexKey, column);
      const fontColor = getFontColorFromValuesAndColumnType(
        columnType,
        value,
        comparedIndexValue
      );
      return {
        type: cellType,
        value: value,
        fontColor: fontColor,
      };
    }
    return {
      type: cellType,
      value: displayIndexValue(record.key as string, column),
    };
  };

  return (
    <TableContainer>
      <StyledTable
        dataSource={dataSource}
        rowKey="key"
        pagination={{ pageSize: 16 }}
        scroll={{ y: "min(58vh, 600px)", x: "max-content" }}
        isLoading={isLoading}
      >
        {columns.map((column) => (
          <Column
            title={column.displayValue}
            key={column.key as string}
            dataIndex={column.key as string}
            fixed={column.fixed}
            render={(
              value: string | number,
              record: MyFundsDataSourceType
            ): JSX.Element => (
              <>
                {isRecordIndex(record, column) && !isLoading ? (
                  <div style={{ width: "fit-content" }}>
                    <CustomDropdown
                      options={addIndexSelectorName(
                        buildIndexOptions(indexes),
                        record.key as string
                      )}
                      onSelectOption={OnSelectedIndex}
                      isInTable={true}
                      override={selectedDefaultBenchmark}
                    />
                  </div>
                ) : (
                  <CustomTableCell
                    {...getCustomCellData(
                      record,
                      column,
                      value || MESSAGES.NOT_AVAILABLE
                    )}
                  />
                )}
              </>
            )}
          />
        ))}
      </StyledTable>
    </TableContainer>
  );
};

type MyFundsTableProps = {
  selectedColumns: Option[];
  fundsData: Metric[];
  selectedDefaultBenchmark: Option;
};

export type SelectedIndexes = {
  [key: string]: Option;
};
